import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GenericChartCardComponent } from 'src/app/shared/component/generic-card/generic-chart-card/generic-chart-card.component';
import { BarChartComponent } from 'src/app/shared/component/charts/bar-chart/bar-chart.component';
import { DateRangeComponent } from 'src/app/shared/component/date-range/date-range.component';
import { FilterFormGroup } from 'src/app/shared/component/base-filter-component/base-filter-component';
import { BaseChart, DashboardReportsCharts } from 'src/app/core/data/model';
import { ChartCardCallerCallBack } from 'src/app/shared/bloc/chart-card-bloc';
import { BaseComponent } from 'src/app/shared/component/base-component/base.component';
import { map, takeUntil } from 'rxjs';
import { DashboardRepository } from 'src/app/core/data/repository';
import { MatIconModule } from '@angular/material/icon';
import { ChartCardHeaderDataComponent } from 'src/app/shared/component/generic-card/chart-card-header-data/chart-card-header-data.component';
import { chartFilters, ChartHeaderData } from 'src/app/shared/interfaces';

@Component({
  selector: 'app-students-report-chart',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatIconModule,
    GenericChartCardComponent,
    BarChartComponent,
    DateRangeComponent,
    ChartCardHeaderDataComponent,
  ],
  templateUrl: './students-report-chart.component.html',
  styleUrls: ['./students-report-chart.component.scss'],
})
export class StudentsReportChartComponent extends BaseComponent implements OnInit {
  form!: FilterFormGroup;
  headerData: ChartHeaderData[];
  chartfilters: chartFilters;
  caller: ChartCardCallerCallBack<BaseChart>;

  constructor(injector: Injector, private dashboardRepository: DashboardRepository) {
    super(['DASHBOARD'], injector, true);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.onSearch();
  }

  private updateChartFilters(): void {
    const dateRange = this.form.get('date').value;
    this.chartfilters = { dateRange };
  }

  private initializeForm(): void {
    this.form = new FilterFormGroup({
      date: new FormControl(null),
    });
  }

  private onSearch(): void {
    this.form.formSubmitted.pipe(takeUntil(this.destroy$)).subscribe((emitted) => {
      if (emitted == 'reset') {
        this.form.reset();
      }
      this.updateChartFilters();
      this.callCaller();
      this.injector.get(ChangeDetectorRef).detectChanges();
    });
  }

  private callCaller(): void {
    this.caller = () => {
      let dateRange = {
        fromDate: this.form.get('date').value?.start ? new Date(this.form.get('date').value?.start) : null,
        toDate: this.form.get('date').value?.end ? new Date(this.form.get('date').value?.end) : null,
      };
      return this.dashboardRepository.getActiveStudentsReport(dateRange).pipe(
        map((res) => {
          return this.constructChart(res);
        })
      );
    };
  }

  private constructChart(res: DashboardReportsCharts): BaseChart[] {
    const activeStudentsChart = new BaseChart(
      [res.countOfActiveMaleStudents, res.countOfActiveFemaleStudents],
      this.translationsList['DASHBOARD']['CHARTS']['ACTIVE'],
      this.currentThemeColors.PRIMARY_COLOR,
      null,
      [res.changeRateOfActiveMaleStudents, res.changeRateOfActiveFemaleStudents]
    );
    const blockedStudentsChart = new BaseChart(
      [res.countOfBlockedMaleStudents, res.countOfBlockedFemaleStudents],
      this.translationsList['DASHBOARD']['CHARTS']['BLOCKED'],
      this.currentThemeColors.GREY_COLOR_400
    );
    // const onlineStudentsChart = new BaseChart(
    //   [res.countOfOnlineMaleStudents, res.countOfOnlineFemaleStudents],
    //   this.translationsList['DASHBOARD']['CHARTS']['ONLINE'],
    //   this.currentThemeColors.ORANGE_COLOR);
    const busyStudentsChart = new BaseChart(
      [res.countOfBusyMaleStudents, res.countOfBusyFemaleStudents],
      this.translationsList['DASHBOARD']['CHARTS']['BUSY'],
      this.currentThemeColors.GREY_COLOR_700
    );
    const chartData = [activeStudentsChart, blockedStudentsChart /*, onlineStudentsChart*/, busyStudentsChart];
    this.initializeChartHeader(chartData);
    return chartData;
  }

  private initializeChartHeader(chartData: BaseChart[]): void {
    const chart: BaseChart = chartData[0];
    this.headerData = [
      {
        label: this.translationsList['DASHBOARD']['CHARTS']['ACTIVE'],
        count: chart.dataSet?.[0] + chart.dataSet?.[1],
      },
      {
        label: this.translationsList['DASHBOARD']['CHARTS']['MALE'],
        count: chart.dataSet?.[0],
        changeRate: chart.changeRate?.[0],
      },
      {
        label: this.translationsList['DASHBOARD']['CHARTS']['FEMALE'],
        count: chart.dataSet?.[1],
        changeRate: chart.changeRate?.[1],
      },
    ];
  }
}
